import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import {
  Newspaper,
  RefreshCw,
  ExternalLink,
  Clock,
  TrendingUp,
  TrendingDown,
  Minus,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const SOURCE_COLORS = {
  benzinga: "bg-orange-500/20 text-orange-300 border-orange-500/40",
  thestreet: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  marketbeat: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
};

const SENTIMENT_STYLE = {
  BULLISH: { icon: TrendingUp, cls: "text-emerald-400" },
  BEARISH: { icon: TrendingDown, cls: "text-red-400" },
  NEUTRAL: { icon: Minus, cls: "text-gray-400" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function News() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all"); // "all" | "my_stocks"
  const [source, setSource] = useState("all");

  const fetchFeed = useCallback(async () => {
    try {
      const res = await api.get(`/news/feed?filter=${filter}&source=${source}&limit=100`);
      setData(res.data);
    } catch (e) {
      console.error("news error", e);
      toast.error("Failed to load news");
    } finally {
      setLoading(false);
    }
  }, [filter, source]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post("/news/refresh");
      toast.success("Fetching latest news…");
      setTimeout(fetchFeed, 25000);
    } catch {
      toast.error("Refresh failed");
    } finally {
      setTimeout(() => setRefreshing(false), 25000);
    }
  };

  useEffect(() => {
    fetchFeed();
    const t = setInterval(fetchFeed, 300000); // 5 min auto-refresh
    return () => clearInterval(t);
  }, [fetchFeed]);

  const articles = data?.articles || [];
  const pinnedCount = data?.pinned_count || 0;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="news-page">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight flex items-center gap-3">
            <Newspaper className="w-8 h-8 text-cyan-400" />
            SOHEL NEWS
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Aggregated from Benzinga · TheStreet · MarketBeat · AI takeaway on your tickers
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          className="gap-2"
          data-testid="news-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      {/* Filter controls */}
      <Card className="card-dark border-cyan-500/20 bg-cyan-500/5">
        <CardContent className="p-3 space-y-3">
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList className="bg-background/50">
              <TabsTrigger value="my_stocks" data-testid="news-filter-mystocks">
                🎯 My Stocks {pinnedCount > 0 && filter === "my_stocks" && `(${pinnedCount})`}
              </TabsTrigger>
              <TabsTrigger value="all" data-testid="news-filter-all">All News</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex flex-wrap gap-2" data-testid="news-source-tabs">
            {[
              { key: "all", label: "All Sources" },
              { key: "benzinga", label: "Benzinga" },
              { key: "thestreet", label: "TheStreet" },
              { key: "marketbeat", label: "MarketBeat" },
            ].map((s) => (
              <button
                key={s.key}
                onClick={() => setSource(s.key)}
                data-testid={`news-source-${s.key}`}
                className={`px-3 py-1 rounded-sm text-xs font-bold border transition-colors ${
                  source === s.key
                    ? "bg-cyan-500/30 border-cyan-400 text-cyan-100"
                    : "bg-transparent border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                }`}
              >
                {s.label}
              </button>
            ))}
            {data?.updated_at && (
              <div className="ml-auto text-xs text-muted-foreground self-center">
                Updated {timeAgo(data.updated_at)} · {articles.length} articles
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Feed */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-40 skeleton rounded-sm" />
          ))}
        </div>
      ) : articles.length === 0 ? (
        <Card className="card-dark border-gray-700">
          <CardContent className="p-8 text-center">
            <Newspaper className="w-12 h-12 text-gray-500 mx-auto mb-3" />
            <p className="text-muted-foreground">No articles match your filters</p>
            <p className="text-xs text-gray-600 mt-1">Try "All News" or a different source</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {articles.map((a, idx) => {
            const sentiment = SENTIMENT_STYLE[a.ai_sentiment] || SENTIMENT_STYLE.NEUTRAL;
            const SentimentIcon = sentiment.icon;
            const isPinned = (a.tickers_mentioned || []).length > 0 && idx < pinnedCount;
            return (
              <a
                key={a.article_id}
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`news-card-${a.article_id}`}
                className={`block rounded-sm border transition-colors ${
                  isPinned
                    ? "border-yellow-500/40 bg-yellow-500/5 hover:border-yellow-400/70"
                    : "border-border bg-card hover:border-cyan-500/40"
                }`}
              >
                <div className="p-4 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                      variant="outline"
                      className={`text-[10px] px-2 py-0 ${SOURCE_COLORS[a.source] || ""}`}
                    >
                      {a.source_display}
                    </Badge>
                    {isPinned && (
                      <Badge variant="outline" className="text-[10px] px-2 py-0 border-yellow-500/50 text-yellow-400">
                        ⭐ YOUR TICKER
                      </Badge>
                    )}
                    <div className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      {timeAgo(a.published_at)}
                    </div>
                    <ExternalLink className="w-3 h-3 text-muted-foreground" />
                  </div>
                  <h3 className="text-sm font-bold text-white leading-snug line-clamp-2">
                    {a.title}
                  </h3>
                  {a.ai_takeaway && (
                    <div className="flex items-start gap-2 p-2 bg-cyan-500/10 border border-cyan-500/20 rounded-sm">
                      <Sparkles className="w-3.5 h-3.5 text-cyan-400 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-cyan-100 leading-relaxed">
                        {a.ai_takeaway}
                      </p>
                      <SentimentIcon className={`w-3.5 h-3.5 ${sentiment.cls} flex-shrink-0 ml-auto`} />
                    </div>
                  )}
                  {!a.ai_takeaway && a.summary && (
                    <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                      {a.summary}
                    </p>
                  )}
                  {(a.tickers_mentioned || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {a.tickers_mentioned.slice(0, 6).map((t) => (
                        <span
                          key={t}
                          className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-purple-500/15 text-purple-300 border border-purple-500/30"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
